import { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Avatar,
    Dialog,
    DialogTitle,
    List,
    ListItemText,
    ListItemButton,
} from '@mui/material';
import { AppContext, SttList } from '../all';

type Sport = {
    id: number;
};

type Privilege = {
    id: number;
    name: string;
    code?: string;
    sport?: Sport;
};

type PrivilegesResponse = {
    rows: Privilege[];
};

type Props = {
    privileges: Privilege[];
    scope?: string;
    onAdd?: (privilege: Privilege) => void;
    onDelete?: (privilege: Privilege) => void;
};

/**
 *    Privilege list
 *    ============
 *
 *    Props
 *
 *        * array privileges
 *        * string scopr: on selecting privilege dialog only show with this "scope"
 *        * function onAdd(privilege)
 *        * function onDelete(privilege)
 */
export function SttPrivilegePureList({
    privileges,
    scope,
    onAdd,
    onDelete,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [selectingPrivilegeOpen, setSelectingPrivilegeOpen] = useState(false);
    const [allPrivileges, setAllPrivileges] = useState<Privilege[]>();

    /**
     * Open adding privilege
     */
    const openAddingPrivilege = () => {
        setSelectingPrivilegeOpen(true);
    };

    /**
     * Load all privileges
     */
    useEffect(() => {
        // Fetch all privileges on the sports center
        if (selectingPrivilegeOpen && !allPrivileges) {
            cxt?.api('GET', `/scs/${cxt?.sc?.id}/privileges`, {
                params: {
                    scope,
                    rows: 200,
                },
                success: (r: PrivilegesResponse) => {
                    setAllPrivileges(r.rows);
                },
            });
        }
    }, [selectingPrivilegeOpen, allPrivileges, cxt, scope]);

    /**
     * Select privilege
     */
    const selectPrivilege = (privilege: Privilege) => {
        setSelectingPrivilegeOpen(false);
        onAdd?.(privilege);
    };

    // Helper function to get translated privilege name
    const getPrivilegeTranslation = (privilege: Privilege): string => {
        // Use code field for translation lookup
        let privilegeCode = privilege.code || privilege.name || '';

        // If the name already contains the full translation key path, extract just the privilege code
        // e.g., "hart.privilege.accounting.name" -> "accounting"
        if (privilegeCode.startsWith('hart.privilege.')) {
            privilegeCode = privilegeCode
                .replace('hart.privilege.', '')
                .replace('.name', '')
                .replace('.desc', '');
        }

        // Normalize key to lowercase for consistent translation lookup
        const normalizedKey = privilegeCode.toLowerCase();
        return t(`hart.privilege.${normalizedKey}.name`, {
            defaultValue: privilege.name,
        });
    };

    // -----| Render |-----
    return (
        <>
            <SttList
                title={t('Privileges')}
                onAdd={onAdd ? () => openAddingPrivilege() : undefined}
                onDelete={onDelete}
                data={
                    privileges &&
                    privileges.map((privilege: Privilege) => ({
                        avatar: privilege.sport ? (
                            <Avatar
                                style={{
                                    width: 30,
                                    height: 30,
                                    borderWidth: 1,
                                    borderStyle: 'solid',
                                    borderColor: '#ddd',
                                    padding: 2,
                                }}
                                src={cxt?.getResource(
                                    `/sports/${privilege.sport.id}.png`,
                                )}
                            />
                        ) : undefined,
                        caption: getPrivilegeTranslation(privilege),
                        objToSelect: privilege,
                    }))
                }
            />

            <Dialog
                onClose={() => setSelectingPrivilegeOpen(false)}
                open={selectingPrivilegeOpen}
            >
                <DialogTitle>{t('Privileges')}</DialogTitle>

                <List>
                    {allPrivileges
                        ?.filter(
                            (e: Privilege) =>
                                !privileges
                                    .map((e2: Privilege) => e2.id)
                                    .includes(e.id),
                        )
                        .map((privilege: Privilege) => (
                            <ListItemButton
                                key={privilege.id}
                                onClick={() => selectPrivilege(privilege)}
                            >
                                <ListItemText
                                    primary={getPrivilegeTranslation(privilege)}
                                />
                            </ListItemButton>
                        ))}
                </List>
            </Dialog>
        </>
    );
}
